import {
    ENDPOINTS,
    QUERYKEYS,
    ENDPOINT_METHODS,
    createEndpointUrl,
    normalizeArticlePaidValue,
    normalizeArticleSectionValue,
} from "./NewsQueueApiConstants";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { createFetchWrapper, getAccessToken } from "./WebUtils";
import { localFetch } from "./LocalNewsQueueFetch";
// @TEMPLATE_@MSAL - Uncomment this line to use MSAL for authentication.
import { useMsal } from "@azure/msal-react";

const DEBUG = true;

const NEWS_QUEUE_BACKEND_URL = process.env.REACT_APP_NEWS_QUEUE_BACKEND_URL;

const fetchWrapper = createFetchWrapper({
    createEndpointUrl,
    localFetch,
    endpointMethods: ENDPOINT_METHODS,
    backendUrl: NEWS_QUEUE_BACKEND_URL,
});

function resolveNewsParameters(parameters, newsId, news_id, id) {
    return (
        parameters ??
        {
            news_id: news_id ?? newsId ?? id,
        }
    );
}

async function releaseNewsLockRequest({ parameters, msal }) {
    const accessToken = await getAccessToken(msal.instance);
    const resolvedBackendUrl = NEWS_QUEUE_BACKEND_URL ?? process.env.REACT_APP_BACKEND_URL;
    const fetchUrl = `${resolvedBackendUrl}/${createEndpointUrl({
        endpoint: ENDPOINTS.releaseNewsLock,
        parameters,
    })}`;

    const fetchParameters = {
        method: ENDPOINT_METHODS.releaseNewsLock,
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
        },
    };

    let response = null;
    if (!resolvedBackendUrl) {
        response = await localFetch({
            endpoint: ENDPOINTS.releaseNewsLock,
            fetchUrl,
            fetchParameters,
            parameters,
        });
    } else {
        response = await fetch(fetchUrl, fetchParameters);
    }

    if (!response || !response.ok) {
        const errorText = await response.text().catch(() => "");
        const status = response?.status ?? "Unknown";
        throw new Error(
            `NewsQueueApi :: releaseNewsLock :: Error response (${status}) from fetch: ${fetchUrl} :: ${errorText}`
        );
    }
}

function assertNewsHasRequiredFieldsForRequest(news) {
    if (!news) {
        throw new Error("NewsQueueApi :: expected 'news' object");
    }

    const requiredFields = [
        "article_header",
        "article_ingress",
        "source_type",
        "target_media",
        "category",
        "timestamp_article",
        "publish_state",
        "article_body",
        "article_author",
        "article_footer",
        "sources",
    ];

    const missing = requiredFields.filter((key) => news[key] == null);
    if (missing.length > 0) {
        throw new Error(
            `NewsQueueApi :: cannot create NewsRequest payload, missing: ${missing.join(", ")}`
        );
    }
}

function normalizeImageDataForRequest(imageData) {
    const raw = String(imageData ?? "").trim();
    if (!raw) return null;

    // Accept both "data:image/...;base64,..." and plain base64 payloads.
    const dataUrlMatch = raw.match(/^data:image\/[a-z0-9.+-]+;base64,(.*)$/i);
    if (dataUrlMatch) {
        const payload = String(dataUrlMatch[1] ?? "").trim();
        return payload.length > 0 ? payload : null;
    }

    return raw;
}

/**
 * Convert a NewsResponse (or an equivalent object) into a NewsRequest payload.
 * NOTE: PUT /news/{news_id} expects a full NewsRequest body, not a partial patch.
 */
export function toNewsRequestPayload(news) {
    assertNewsHasRequiredFieldsForRequest(news);

    const normalizedImageData = normalizeImageDataForRequest(news.image_data);

    return {
        article_header: news.article_header,
        article_ingress: news.article_ingress,
        source_type: news.source_type,
        target_media: news.target_media,
        category: news.category,
        timestamp_article: news.timestamp_article,
        publish_state: news.publish_state,

        article_body: news.article_body,
        article_background: news.article_background ?? null,
        article_author: news.article_author,
        article_footer: news.article_footer,
        article_paid: normalizeArticlePaidValue(news.article_paid),
        ai_clause: news.ai_clause ?? true,
        article_section: normalizeArticleSectionValue(news.article_section),
        photographer: String(news.photographer ?? ""),
        caption: String(news.caption ?? ""),
        image_data: normalizedImageData,

        sources: news.sources,

        publish_time: news.publish_time ?? null,
        approver: news.approver ?? null,
        feedback: news.feedback ?? null,
        multi_publication_target_medias: news.multi_publication_target_medias || [],
    };
}

export function useListNews({ parameters, enabled = true } = {}) {
    return useListNewsWithParams({ parameters, enabled });
}

function normalizeAfterValue(after) {
    if (after == null) return undefined;
    if (after instanceof Date) return after.toISOString();
    const value = String(after).trim();
    return value.length > 0 ? value : undefined;
}

export function useListNewsWithParams({
    parameters,
    enabled = true,
    refetchInterval,
    refetchIntervalInBackground = false,
    staleTime,
    refetchOnWindowFocus = false,
} = {}) {
    // @TEMPLATE_@MSAL - Uncomment this line to use MSAL for authentication.
    const msal = useMsal();

    return useQuery({
        queryKey: [QUERYKEYS.listNews, parameters ?? {}],
        queryFn: async ({ queryKey }) => {
            if (DEBUG) console.log("Api :: listNews");

            const [_key, parameters] = queryKey;

            const result = await fetchWrapper({
                endpoint: ENDPOINTS.listNews,
                parameters,
                msal,
            });

            return { queryKey: _key, result };
        },
        enabled,
        staleTime,
        refetchInterval,
        refetchIntervalInBackground,
        refetchOnWindowFocus,
    });
}

/**
 * Convenience hook for delta-polling newly created news.
 * Uses GET /news with the `after` query parameter.
 */
export function useListNewsDelta({
    after,
    parameters,
    enabled = true,
    refetchInterval = 5000,
    refetchIntervalInBackground = false,
    staleTime = 0,
} = {}) {
    const normalizedAfter = normalizeAfterValue(after);

    const mergedParameters = {
        ...(parameters ?? {}),
        ...(normalizedAfter ? { after: normalizedAfter } : {}),
    };

    return useListNewsWithParams({
        parameters: mergedParameters,
        enabled: enabled && Boolean(normalizedAfter),
        refetchInterval,
        refetchIntervalInBackground,
        staleTime,
        refetchOnWindowFocus: false,
    });
}

export function shouldRetryGetNews(failureCount, error) {
    return ![400, 401, 403, 404, 422].includes(error?.status) && failureCount < 3;
}

export function useGetNews({ parameters, enabled = true }) {
    // @TEMPLATE_@MSAL - Uncomment this line to use MSAL for authentication.
    const msal = useMsal();

    return useQuery({
        queryKey: [QUERYKEYS.getNews, parameters ?? {}],
        queryFn: async ({ queryKey }) => {
            if (DEBUG) console.log("Api :: getNews");

            const [_key, parameters] = queryKey;

            const result = await fetchWrapper({
                endpoint: ENDPOINTS.getNews,
                parameters,
                msal,
            });

            return { queryKey: _key, result };
        },
        enabled,
        retry: shouldRetryGetNews,
        refetchOnWindowFocus: false,
    });
}

export function useAddNews({ onSuccess, onError } = {}) {
    // @TEMPLATE_@MSAL - Uncomment this line to use MSAL for authentication.
    const msal = useMsal();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (body) => {
            if (DEBUG) console.log("Api :: addNews", body);

            const result = await fetchWrapper({
                endpoint: ENDPOINTS.addNews,
                body,
                msal,
            });

            return { result };
        },
        onSuccess: (result) => {
            if (DEBUG) console.log("NewsQueueApi :: addNews :: onSuccess");
            if (DEBUG) console.log(result);

            queryClient.invalidateQueries({ queryKey: [QUERYKEYS.listNews] });

            if (onSuccess) onSuccess(result);
        },
        onError: (error) => {
            if (DEBUG) console.log("NewsQueueApi :: addNews :: onError");
            if (DEBUG) console.log(error);
            if (onError) onError(error);
        },
    });
}

export function useUpdateNews({ onSuccess, onError } = {}) {
    // @TEMPLATE_@MSAL - Uncomment this line to use MSAL for authentication.
    const msal = useMsal();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ parameters, body, newsId, news_id, id }) => {
            if (DEBUG) console.log("Api :: updateNews", { parameters, body, newsId, news_id, id });

            const resolvedParameters = resolveNewsParameters(parameters, newsId, news_id, id);

            const result = await fetchWrapper({
                endpoint: ENDPOINTS.updateNews,
                parameters: resolvedParameters,
                body,
                msal,
            });

            return { result, resolvedParameters };
        },
        onSuccess: (result, variables) => {
            if (DEBUG) console.log("NewsQueueApi :: updateNews :: onSuccess");
            if (DEBUG) console.log(result);

            queryClient.invalidateQueries({ queryKey: [QUERYKEYS.listNews] });
            queryClient.invalidateQueries({ queryKey: [QUERYKEYS.listNewsLocks] });
            queryClient.invalidateQueries({
                queryKey: [QUERYKEYS.getNews, result?.resolvedParameters ?? variables?.parameters ?? {}],
            });

            if (onSuccess) onSuccess(result);
        },
        onError: (error) => {
            if (DEBUG) console.log("NewsQueueApi :: updateNews :: onError");
            if (DEBUG) console.log(error);
            if (onError) onError(error);
        },
    });
}

/**
 * PATCH /news/{news_id} with NewsPatchRequest.
 * Current API supports only publish_state='rejected' (+ optional feedback).
 */
export function usePatchNews({ onSuccess, onError } = {}) {
    const msal = useMsal();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ parameters, body, newsId, news_id, id }) => {
            if (DEBUG) console.log("Api :: patchNews", { parameters, body, newsId, news_id, id });

            const resolvedParameters = resolveNewsParameters(parameters, newsId, news_id, id);

            const result = await fetchWrapper({
                endpoint: ENDPOINTS.patchNews,
                parameters: resolvedParameters,
                body,
                msal,
            });

            return { result, resolvedParameters };
        },
        onSuccess: (result, variables) => {
            if (DEBUG) console.log("NewsQueueApi :: patchNews :: onSuccess");
            if (DEBUG) console.log(result);

            queryClient.invalidateQueries({ queryKey: [QUERYKEYS.listNews] });
            queryClient.invalidateQueries({ queryKey: [QUERYKEYS.listNewsLocks] });
            queryClient.invalidateQueries({
                queryKey: [QUERYKEYS.getNews, result?.resolvedParameters ?? variables?.parameters ?? {}],
            });

            if (onSuccess) onSuccess(result);
        },
        onError: (error) => {
            if (DEBUG) console.log("NewsQueueApi :: patchNews :: onError");
            if (DEBUG) console.log(error);
            if (onError) onError(error);
        },
    });
}

export function useGetConfigurations({ enabled = true, staleTime = 60_000 } = {}) {
    const msal = useMsal();

    return useQuery({
        queryKey: [QUERYKEYS.getConfigurations, {}],
        queryFn: async ({ queryKey }) => {
            if (DEBUG) console.log("Api :: getConfigurations");

            const [_key] = queryKey;

            const result = await fetchWrapper({
                endpoint: ENDPOINTS.getConfigurations,
                parameters: {},
                msal,
            });

            return { queryKey: _key, result };
        },
        enabled,
        staleTime,
        refetchOnWindowFocus: false,
    });
}

export function useListNewsLocks({ enabled = true, refetchInterval = 5000 } = {}) {
    const msal = useMsal();

    return useQuery({
        queryKey: [QUERYKEYS.listNewsLocks, {}],
        queryFn: async ({ queryKey }) => {
            if (DEBUG) console.log("Api :: listNewsLocks");

            const [_key] = queryKey;

            const result = await fetchWrapper({
                endpoint: ENDPOINTS.listNewsLocks,
                parameters: {},
                msal,
            });

            return { queryKey: _key, result };
        },
        enabled,
        refetchInterval,
        refetchOnWindowFocus: false,
        refetchIntervalInBackground: false,
    });
}

export function useAcquireNewsLock({ onSuccess, onError } = {}) {
    const msal = useMsal();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ parameters, newsId, news_id, id }) => {
            if (DEBUG) console.log("Api :: acquireNewsLock", { parameters, newsId, news_id, id });

            const resolvedParameters = resolveNewsParameters(parameters, newsId, news_id, id);

            const result = await fetchWrapper({
                endpoint: ENDPOINTS.acquireNewsLock,
                parameters: resolvedParameters,
                msal,
            });

            return { result, resolvedParameters };
        },
        onSuccess: (result, variables) => {
            if (DEBUG) console.log("NewsQueueApi :: acquireNewsLock :: onSuccess");
            if (DEBUG) console.log(result);

            const resolvedParameters = result?.resolvedParameters ?? variables?.parameters ?? {};

            queryClient.invalidateQueries({ queryKey: [QUERYKEYS.listNewsLocks] });
            queryClient.invalidateQueries({ queryKey: [QUERYKEYS.getNews, resolvedParameters] });

            if (onSuccess) onSuccess(result);
        },
        onError: (error) => {
            if (DEBUG) console.log("NewsQueueApi :: acquireNewsLock :: onError");
            if (DEBUG) console.log(error);
            if (onError) onError(error);
        },
    });
}

export function useReleaseNewsLock({ onSuccess, onError } = {}) {
    const msal = useMsal();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ parameters, newsId, news_id, id }) => {
            if (DEBUG) console.log("Api :: releaseNewsLock", { parameters, newsId, news_id, id });

            const resolvedParameters = resolveNewsParameters(parameters, newsId, news_id, id);

            await releaseNewsLockRequest({ parameters: resolvedParameters, msal });

            return { result: null, resolvedParameters };
        },
        onSuccess: (result, variables) => {
            if (DEBUG) console.log("NewsQueueApi :: releaseNewsLock :: onSuccess");
            if (DEBUG) console.log(result);

            const resolvedParameters = result?.resolvedParameters ?? variables?.parameters ?? {};

            queryClient.invalidateQueries({ queryKey: [QUERYKEYS.listNewsLocks] });
            queryClient.invalidateQueries({ queryKey: [QUERYKEYS.getNews, resolvedParameters] });

            if (onSuccess) onSuccess(result);
        },
        onError: (error) => {
            if (DEBUG) console.log("NewsQueueApi :: releaseNewsLock :: onError");
            if (DEBUG) console.log(error);
            if (onError) onError(error);
        },
    });
}

/**
 * Decline / revert-decline helpers.
 * Decline uses PATCH /news/{news_id}.
 * Revert still uses full PUT payload by setting publish_state back to 'new'.
 */
export function useDeclineNews({ onSuccess, onError } = {}) {
    const patchNews = usePatchNews({ onSuccess, onError });
    const updateNews = useUpdateNews({ onSuccess, onError });

    const decline = ({ news, feedback }) => {
        return patchNews.mutate({
            id: news?.id,
            body: {
                publish_state: "rejected",
                feedback: feedback ?? null,
            },
        });
    };

    const revertDecline = ({ news, clearFeedback = true }) => {
        return updateNews.mutate({
            id: news?.id,
            body: toNewsRequestPayload({
                ...news,
                publish_state: "new",
                feedback: clearFeedback ? null : news?.feedback ?? null,
            }),
        });
    };

    return {
        ...patchNews,
        decline,
        revertDecline,
    };
}

/**
 * Accept helpers.
 * The API models acceptance via publish_state + optional publish_time.
 */
export function useAcceptNews({ onSuccess, onError } = {}) {
    const updateNews = useUpdateNews({ onSuccess, onError });

    const acceptNow = ({ news, feedback, approver }) => {
        return updateNews.mutate({
            id: news?.id,
            body: toNewsRequestPayload({
                ...news,
                publish_state: "published",
                publish_time: null,
                feedback: feedback ?? null,
                approver: approver ?? news?.approver ?? null,
            }),
        });
    };

    const acceptAsDraft = ({ news, feedback, approver }) => {
        return updateNews.mutate({
            id: news?.id,
            body: toNewsRequestPayload({
                ...news,
                publish_state: "draft",
                publish_time: null,
                feedback: feedback ?? null,
                approver: approver ?? news?.approver ?? null,
            }),
        });
    };

    const acceptScheduled = ({ news, publishTimeIso, feedback, approver }) => {
        return updateNews.mutate({
            id: news?.id,
            body: toNewsRequestPayload({
                ...news,
                publish_state: "published",
                publish_time: publishTimeIso,
                feedback: feedback ?? null,
                approver: approver ?? news?.approver ?? null,
            }),
        });
    };

    return {
        ...updateNews,
        acceptNow,
        acceptAsDraft,
        acceptScheduled,
    };
}
